import axios from 'axios';
import AppStore from '../store';
import * as ActionCreators from '../redux/action';
import { LS_ACCESS_TOKEN } from '../constants';

class Api {
  constructor() {
    this.axios = axios.create({
      timeout: process.env.REACT_APP_AXIOS_TIMEOUT,
    });
    /**
     * Handling our server responses, when errors, get verbose ones
     */
    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Client-zone tokens are long-lived (no refresh flow, same as backoffice) -
        // an invalid/expired token just logs the user out.
        if (error?.response?.status === 401) {
          AppStore.dispatch(ActionCreators.logOut({ force: true }));
        }
        // Create verbose error, not just error message
        return Promise.reject(error.response);
      }
    );
  }
}

class FaqApi extends Api {
  constructor(data) {
    super(data);
    this.baseURL = process.env.REACT_APP_API_BASE_URI;

    this.axios.interceptors.request.use(
      (config) => {
        config.headers = {
          accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem(LS_ACCESS_TOKEN)}`,
        };
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /*  Get all FAQ's */
  getFAQs = () => this.axios.get(`${this.baseURL}/en/faq/questions`);

  /* Get all FAQ's with responses by categories */
  getFAQsCategories = () => this.axios.get(`${this.baseURL}/en/faq/categories`);

  /* Get most frequented search terms */
  getMostSearchedFAQsTerms = ({ limitTerms }) =>
    this.axios.get(`${this.baseURL}/en/faq/frequently-searched/${limitTerms}`);

  /* Single FAQ Answer */
  getFAQsAnswer = ({ faqId }) => this.axios.get(`${this.baseURL}/en/faq/answer/${faqId}`);

  /* Search FAQ's */
  getSearchedFAQs = ({ searchedTerm }) =>
    this.axios.get(`${this.baseURL}/en/faq/search/${searchedTerm}`);
}

class LoginApi extends Api {
  constructor(data) {
    super(data);
    this.wishmakerApiUrl = process.env.REACT_APP_WISHMAKER_API_URI;
  }

  signIn = (data) => this.axios.post(`${this.wishmakerApiUrl}/client/login`, data);
}

class AuthApi extends Api {
  constructor(data) {
    super(data);
    this.wishmakerApiUrl = process.env.REACT_APP_WISHMAKER_API_URI;

    this.axios.interceptors.request.use(
      (config) => {
        config.headers = {
          Authorization: `Bearer ${localStorage.getItem(LS_ACCESS_TOKEN)}`,
        };
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  logout = () => this.axios.post(`${this.wishmakerApiUrl}/client/logout`, {});
}

class AreaApi extends Api {
  constructor(data) {
    super(data);
    this.wishmakerApiUrl = process.env.REACT_APP_WISHMAKER_API_URI;

    this.axios.interceptors.request.use(
      (config) => {
        config.headers = {
          accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem(LS_ACCESS_TOKEN)}`,
        };
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /* Dashboard Calls */
  getBorrowerDashboardData = () => this.axios.get(`${this.wishmakerApiUrl}/user-area/detail`);

  /* Borrower Onboarding Calls */
  sendPersonalInformationProcessingAgrees = (data, wmNumber) =>
    this.axios.post(`${this.wishmakerApiUrl}/personal-information-processing/${wmNumber}`, data, {
      headers: { 'Content-type': 'multipart/form-data' },
    });

  getBorrowerOnboardingStep = () => this.axios.get(`${this.wishmakerApiUrl}/onboarding`);

  sendBorrowerOnboardingStep = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/onboarding`, data);

  sendBorrowerOnboardingDocuments = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/onboarding`, data, {
      headers: { 'Content-type': 'multipart/form-data' },
    });

  /* Investor Onboarding Calls */
  getInvestorOnboardingStep = () => this.axios.get(`${this.wishmakerApiUrl}/onboarding-investors`);

  sendInvestorOnboardingStep = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/onboarding-investors`, data);

  sendInvestorOnboardingIdentityCards = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/onboarding-investors`, data, {
      headers: { 'Content-type': 'multipart/form-data' },
    });

  /* SMS */
  sendSms = (data) => this.axios.post(`${this.wishmakerApiUrl}/sms/send`, data);
  sendPublicSms = (data) => this.axios.post(`${this.wishmakerApiUrl}/sms/public/send`, data);
  verifyPhoneNumber = (data) => this.axios.post(`${this.wishmakerApiUrl}/verify-phone-number`, data);

  // Mobile signature token request
  requestMobileSignatureToken = (preLoanId) =>
    this.axios.post(`${this.wishmakerApiUrl}/mobile-signature/pre-loans/${preLoanId}/token`, {});

  getInvestorDocumentTypes = () =>
    this.axios.get(`${this.wishmakerApiUrl}/selects/public/identity-documents`);
  getDebtorDocumentTypes = () =>
    this.axios.get(`${this.wishmakerApiUrl}/selects/public/identity-documents-debtor`);

  /* Mailbox Calls */
  getMailboxDocuments = () => this.axios.get(`${this.wishmakerApiUrl}/person-documents`);
  getMailboxEmails = () => this.axios.get(`${this.wishmakerApiUrl}/person-messages/email`);
  getMailboxSms = () => this.axios.get(`${this.wishmakerApiUrl}/person-messages/sms`);
  getMailboxRequests = () => this.axios.get(`${this.wishmakerApiUrl}/person-request/user`);
  getMailboxLetters = () => this.axios.get(`${this.wishmakerApiUrl}/person-messages/letter`);
  getMailboxAll = () => this.axios.get(`${this.wishmakerApiUrl}/person-messages/all`);
  getMailboxReports = () =>
    this.axios.get(`${this.wishmakerApiUrl}/person-messages/investment_report`);
  setMailboxReportsSubscription = (subscribed) =>
    this.axios.post(`${this.wishmakerApiUrl}/user-area/reports/change-subscription-status`, {
      send_reports: subscribed,
    });

  /* User Calls */
  getUserData = () => this.axios.get(`${this.wishmakerApiUrl}/auth-person-detail`);
  resendPassword = (data) => this.axios.post(`${this.wishmakerApiUrl}/resend-password`, data);
  updateUserData = (data) => this.axios.put(`${this.wishmakerApiUrl}/personal-info`, data);
  updateUserAgreements = (data) => this.axios.put(`${this.wishmakerApiUrl}/agrees`, data);
  /* check user registration status on login */
  checkRegistration = (wmNumber) =>
    this.axios.get(`${this.wishmakerApiUrl}/check-registration/${wmNumber}`);

  sendFinishRegistrationEmail = ({ wmNumber, sendAgain }) =>
    this.axios.get(
      `${this.wishmakerApiUrl}/send-finish-registration-email/${wmNumber}${
        sendAgain ? `?sendAgain=${sendAgain}` : ''
      }`
    );

  /* Investor Dashboard Calls */
  getInvestorDashboardData = () => this.axios.get(`${this.wishmakerApiUrl}/user-area/overview`);

  getInvestorInvestments = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/user-area/investments`, data);

  getInvestorInvestmentDetail = (data, offerId) =>
    this.axios.get(`${this.wishmakerApiUrl}/user-area/investments/${data}`, {
      params: {
        offerId,
      },
    });

  sendInvestorInstruction = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/user-area/investments/instruction`, data);

  sellInvestmentToGuaranteeFund = (data, investment) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/${investment}/guarantee-fund`, data);

  createInvestmentOffer = (data, investment) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/${investment}/offer`, data);

  cancelOfferSell = (data, offer) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/${offer}/cancel-offer`, data);

  getInvestorInvestmentInstruction = () =>
    this.axios.get(`${this.wishmakerApiUrl}/user-area/investments/instruction`);

  getDepositQrCodeString = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/user-area/deposits/qr-code-string`, data);

  getInvestorSecondaryMarket = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/offers`, data);

  getInvestorBiddings = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/biddings`, data);

  buySecondaryMarketOffer = (data, offer) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/${offer}/buy`, data);

  bidSecondaryMarketOffer = (data, offer, biddingId) =>
    this.axios.post(
      `${this.wishmakerApiUrl}/investments/${offer}/make-bid${biddingId ? `/${biddingId}` : ''}`,
      data
    );

  acceptBiddingOffer = (data, bidding) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/accept-bidding/${bidding}`, data);

  declineBiddingOffer = (data, bidding) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/decline-bidding/${bidding}`, data);

  getRequestTemplate = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/person-request/template`, data);

  createRequest = (data) => this.axios.post(`${this.wishmakerApiUrl}/person-request/create`, data);

  getInvestmentsForSale = (value) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/automatic-sale/search`, {
      value,
    });

  postInvestmentsForSale = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/automatic-sale/offer`, data);

  getInvestmentsOverview = () =>
    this.axios.get(`${this.wishmakerApiUrl}/user-area/resources-overview`);

  getEstimatedEarningsGraphData = (data) =>
    this.axios.post(`${this.wishmakerApiUrl}/estimates/earnings-graph-data`, data);

  transferClaim = (data, investmentId) =>
    this.axios.post(`${this.wishmakerApiUrl}/investments/${investmentId}/transfer`, data);

  // Poll for mobile signature readiness by token
  checkMobileSignature = (token) =>
    this.axios.post(`${this.wishmakerApiUrl}/mobile-signature/check`, { token });
}

class PublicAreaApi extends Api {
  constructor(data) {
    super(data);
    this.wishmakerApiUrl = process.env.REACT_APP_WISHMAKER_API_URI;

    this.axios.interceptors.request.use(
      (config) => {
        config.headers = {
          accept: 'application/json',
          'Content-Type': 'application/json',
        };
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  // Submit signature using a public token obtained from requestToken
  submitMobileSignature = ({ token, signature }) =>
    this.axios.post(`${this.wishmakerApiUrl}/mobile-signature/submit`, { token, signature });
}

export const authApi = new AuthApi();
export const loginApi = new LoginApi();
export const areaApi = new AreaApi();
export const faqApi = new FaqApi();
export const publicAreaApi = new PublicAreaApi();

export default new Api();
